import React, { useEffect, useState } from "react";
import { api, fmtEUR } from "../lib/api";
import { Plus, Trash2, Download, X, Wallet } from "lucide-react";
import { toast } from "sonner";

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [banks, setBanks] = useState([]);
  const [pf, setPf] = useState({ amount_eur: 0, bank_id: "", notes: "" });

  const load = async () => {
    const [s, b] = await Promise.all([api.get("/suppliers"), api.get("/banks")]);
    setItems(s.data);
    setBanks(b.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLedger = async (sid) => {
    const r = await api.get(`/suppliers/${sid}/ledger`);
    setLedger(r.data);
  };

  const save = async () => {
    try {
      const payload = { ...editing }; delete payload.id;
      if (editing.id) await api.put(`/suppliers/${editing.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success("Kaydedildi");
      setOpen(false); setEditing(null); load();
    } catch { toast.error("Kaydedilemedi"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/suppliers/${id}`);
    load();
  };

  const addSupplierPayment = async () => {
    try {
      await api.post("/payments", {
        supplier_id: ledger.supplier.id,
        party: "supplier",
        amount_eur: parseFloat(pf.amount_eur) || 0,
        currency: "EUR",
        bank_id: pf.bank_id,
        notes: pf.notes,
      });
      toast.success("Ödeme eklendi");
      setPayOpen(false);
      setPf({ amount_eur: 0, bank_id: "", notes: "" });
      loadLedger(ledger.supplier.id);
    } catch { toast.error("Ödeme eklenemedi"); }
  };

  const exportExcel = async (sid) => {
    const r = await api.get(`/suppliers/${sid}/ledger/export`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tedarikci_cari.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px]" data-testid="suppliers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-overline">Tedarikçi Yönetimi</div>
          <h1 className="mt-2 font-display font-black text-3xl md:text-4xl tracking-tight">Tedarikçiler & Cari</h1>
        </div>
        <button
          data-testid="add-supplier-btn"
          onClick={() => { setEditing({ name: "", contact: "", phone: "", email: "", address: "", notes: "" }); setOpen(true); }}
          className="h-11 px-5 bg-zinc-950 text-white rounded-md hover:bg-zinc-800 flex items-center gap-2 font-medium text-sm"
        >
          <Plus size={16} /> Yeni Tedarikçi
        </button>
      </div>

      <div className="mt-8 grid-card overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-zinc-50 text-left">
            <tr className="border-b border-zinc-200">
              <Th>Ad</Th><Th>İletişim</Th><Th>Telefon</Th><Th>Adres</Th><Th>Email</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={() => loadLedger(s.id)} data-testid={`supplier-row-${s.id}`}>
                <Td className="font-medium">{s.name}</Td>
                <Td>{s.contact || "—"}</Td>
                <Td>{s.phone || "—"}</Td>
                <Td data-testid={`supplier-address-${s.id}`}>{s.address || "—"}</Td>
                <Td>{s.email || "—"}</Td>
                <Td>
                  <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => { setEditing({ ...s }); setOpen(true); }} className="px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100 rounded">Düzenle</button>
                    <button onClick={() => del(s.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 size={13} /></button>
                  </div>
                </Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-sm text-zinc-500">Tedarikçi ekleyin</td></tr>}
          </tbody>
        </table>
      </div>

      {open && editing && (
        <div className="fixed inset-0 z-50 bg-zinc-950/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <div className="bg-white border rounded-md w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-200 flex justify-between">
              <h2 className="font-display font-bold text-xl">{editing.id ? "Düzenle" : "Yeni Tedarikçi"}</h2>
              <button onClick={() => setOpen(false)}><X size={16} /></button>
            </div>
            <div className="p-6 space-y-3">
              {["name", "contact", "phone", "email", "address", "notes"].map((k) => (
                <label key={k} className="block">
                  <div className="text-xs text-zinc-600 mb-1 capitalize">{ { name: "Ad", contact: "Yetkili", phone: "Telefon", email: "Email", address: "Adres", notes: "Notlar" }[k] }</div>
                  <input value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} className="w-full h-10 px-3 border rounded-md text-sm" data-testid={`supplier-field-${k}`} />
                </label>
              ))}
            </div>
            <div className="p-6 border-t flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="h-10 px-4 text-sm border rounded-md">İptal</button>
              <button onClick={save} className="h-10 px-6 text-sm bg-zinc-950 text-white rounded-md font-medium" data-testid="supplier-save-btn">Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {ledger && (
        <div className="fixed inset-0 z-50 bg-zinc-950/40 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto" onClick={() => setLedger(null)}>
          <div className="bg-white border rounded-md w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()} data-testid="supplier-ledger">
            <div className="p-6 border-b border-zinc-200 flex justify-between items-start">
              <div>
                <div className="label-overline">Cari Hesap</div>
                <h2 className="mt-1 font-display font-black text-2xl">{ledger.supplier.name}</h2>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setPayOpen(true)} className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-md flex items-center gap-2" data-testid="supplier-add-payment-btn"><Wallet size={13}/>Ödeme Ekle</button>
                <button onClick={() => exportExcel(ledger.supplier.id)} className="h-9 px-4 text-sm border rounded-md flex items-center gap-2" data-testid="supplier-export-btn"><Download size={13}/>Excel</button>
                <button onClick={() => setLedger(null)}><X size={16} /></button>
              </div>
            </div>

            <div className="grid grid-cols-3 border-b border-zinc-200">
              <M label="Toplam Borç" value={fmtEUR(ledger.total_debt_eur)} />
              <M label="Toplam Ödeme" value={fmtEUR(ledger.total_paid_eur)} tone="green" />
              <M label="Bakiye" value={fmtEUR(ledger.balance_eur)} tone={ledger.balance_eur > 0 ? "red" : "green"} />
            </div>

            {payOpen && (
              <div className="p-6 bg-zinc-50 border-b border-zinc-200">
                <h3 className="font-medium mb-3">Tedarikçiye Ödeme</h3>
                <div className="grid grid-cols-3 gap-3">
                  <label className="block"><div className="text-xs text-zinc-600 mb-1">Tutar (EUR)</div><input type="number" value={pf.amount_eur} onChange={(e) => setPf({ ...pf, amount_eur: e.target.value })} className="w-full h-10 px-3 border rounded-md text-sm" data-testid="supp-pay-eur" /></label>
                  <label className="block"><div className="text-xs text-zinc-600 mb-1">Banka</div><select value={pf.bank_id} onChange={(e) => setPf({ ...pf, bank_id: e.target.value })} className="w-full h-10 px-3 border rounded-md text-sm bg-white" data-testid="supp-pay-bank"><option value="">—</option>{banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></label>
                  <label className="block"><div className="text-xs text-zinc-600 mb-1">Not</div><input value={pf.notes} onChange={(e) => setPf({ ...pf, notes: e.target.value })} className="w-full h-10 px-3 border rounded-md text-sm" data-testid="supp-pay-notes" /></label>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <button onClick={() => setPayOpen(false)} className="h-9 px-3 text-sm border rounded-md">İptal</button>
                  <button onClick={addSupplierPayment} className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-md font-medium" data-testid="supp-pay-save">Kaydet</button>
                </div>
              </div>
            )}

            <div className="p-4 md:p-6 overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-zinc-50">
                  <tr className="border-b border-zinc-200 text-left">
                    <Th>Tarih</Th><Th>Tür</Th><Th>Açıklama</Th><Th>Borç (EUR)</Th><Th>Ödeme (EUR)</Th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.entries.map((e) => (
                    <tr key={`${e.type}-${e.date}-${e.case_id || e.payment_id || e.description}`} className="border-b border-zinc-100">
                      <Td>{(e.date || "").slice(0, 10)}</Td>
                      <Td>{e.type === "borç" ? <span className="text-[#b71c1c]">Borç</span> : <span className="text-[#00913c]">Ödeme</span>}</Td>
                      <Td className="text-xs">{e.description}</Td>
                      <Td>{e.type === "borç" ? fmtEUR(e.amount_eur) : "—"}</Td>
                      <Td>{e.type === "ödeme" ? fmtEUR(e.amount_eur) : "—"}</Td>
                    </tr>
                  ))}
                  {ledger.entries.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm text-zinc-500">Hareket yok</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 label-overline font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const M = ({ label, value, tone = "default" }) => {
  const c = { default: "text-zinc-950", green: "text-[#00913c]", red: "text-[#b71c1c]", amber: "text-[#8a5b00]" }[tone];
  return (
    <div className="p-6 border-r border-zinc-200 last:border-r-0">
      <div className="label-overline">{label}</div>
      <div className={`mt-2 font-display font-black text-2xl ${c}`}>{value}</div>
    </div>
  );
};
